import { Injectable } from '@angular/core';
import type { BulkItemParam, CpaNavigationParams, DumpsterContainerType } from '@shared/ui-service';
import { CustomerType, Frequency, isResidentialServiceCode, ResidentialAddress, ServiceCode } from '@shared/ui-service';

@Injectable({
  providedIn: 'root',
})
export class OrderStepperService {
  public params: CpaNavigationParams | null = null;

  // Commercial Trash params
  public getTrashContainerSize(): number | null {
    return this.params?.trashContainerSize ?? null;
  }

  public getTrashFrequency(): Frequency | null {
    return this.params?.trashFrequency ?? null;
  }

  public getTrashQuantity(): number | null {
    return this.params?.trashQuantity ?? null;
  }

  // Commercial Recycling params
  public getRecyclingContainerSize(): number | null {
    return this.params?.recyclingContainerSize ?? null;
  }

  public getRecyclingFrequency(): Frequency | null {
    return this.params?.recyclingFrequency ?? null;
  }

  public getRecyclingQuantity(): number | null {
    return this.params?.recyclingQuantity ?? null;
  }

  // Commercial Food params
  public getFoodContainerSize(): number | null {
    return this.params?.foodContainerSize ?? null;
  }

  public getFoodFrequency(): Frequency | null {
    return this.params?.foodFrequency ?? null;
  }

  public getFoodQuantity(): number | null {
    return this.params?.foodQuantity ?? null;
  }

  // Shared Commercial param
  public getStartDate(): string | null {
    return this.params?.startDate ?? null;
  }

  // Commercial Bulk params
  public getBulkItems(): BulkItemParam[] | null {
    return this.params?.bulkItems ?? null;
  }

  public getPickupDate(): string | null {
    return this.params?.pickupDate ?? null;
  }

  // Roll-Off (Dumpster) params
  public getDeliveryDate(): string | null {
    return this.params?.deliveryDate ?? null;
  }

  public getDumpsterQuantity(): number | null {
    const quantity = this.params?.dumpsterQuantity;
    return quantity ? Number(quantity) : null;
  }

  public getContainerLocation(): string | null {
    return this.params?.containerLocation ?? null;
  }

  // Compactor params
  public getCompactorMaterials(): string | null {
    return this.params?.compactorMaterials ?? null;
  }

  public getCompactorServiceDate(): string | null {
    return this.params?.compactorServiceDate ?? null;
  }

  public isResidentialInParams(code: ServiceCode): boolean {
    return isResidentialServiceCode(code);
  }

  public isCommercialInParams(code: ServiceCode): boolean {
    const commercialServices = [
      ServiceCode.CommercialRecycling,
      ServiceCode.CommercialTrashAndRecycling,
      ServiceCode.CommercialBulk,
      ServiceCode.CommercialFoodWaste,
      ServiceCode.CommercialTrash,
    ];

    return commercialServices.includes(code);
  }

  public isRollOffInParams(code: ServiceCode): boolean {
    return code === ServiceCode.RollOff;
  }

  public getInitialCustomerType(): CustomerType {
    if (this.params && this.params.customerType) {
      return this.params.customerType as CustomerType;
    }

    return CustomerType.Residential;
  }

  public getInitialContainerType(): DumpsterContainerType | null {
    return this.params?.containerType ?? null;
  }

  public getInitialContainerId(): number | null {
    return this.params?.containerId ?? null;
  }

  public getAddressFromParams(params: CpaNavigationParams): ResidentialAddress {
    return {
      city: params.city,
      state: params.state,
      zip: params.zip,
      street: params.street,
      geoLocation: {
        latitude: params.latitude,
        longitude: params.longitude,
      },
    };
  }
}
